import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import ReactECharts from "echarts-for-react";
import {
  FolderKanban,
  CheckCircle,
  Clock,
  AlertTriangle,
  Users,
  Target,
  Calendar
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

const PMODashboard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed-projects`, {}, { headers: { Authorization: `Bearer ${token}` } });
        const response = await axios.get(`${API}/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProjects(response.data.projects || []);
      } catch (error) {
        console.error("Error fetching PMO data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  // Calculate stage gate distribution from projects
  const stageGateDistribution = projects.reduce((acc, p) => {
    const stage = `Stage ${p.stage_gate || 1}`;
    acc[stage] = (acc[stage] || 0) + 1;
    return acc;
  }, {});

  const stageGateChartOptions = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      borderColor: 'rgba(201, 162, 39, 0.3)',
      textStyle: { color: '#f8fafc' }
    },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '15%', containLabel: true },
    xAxis: {
      type: 'category',
      data: Object.keys(stageGateDistribution),
      axisLine: { lineStyle: { color: '#334155' } },
      axisLabel: { color: '#94a3b8' }
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#1e293b' } },
      axisLabel: { color: '#94a3b8' }
    },
    series: [{
      data: Object.values(stageGateDistribution),
      type: 'bar',
      barWidth: '50%',
      itemStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: '#c9a227' },
            { offset: 1, color: 'rgba(201, 162, 39, 0.3)' }
          ]
        },
        borderRadius: [4, 4, 0, 0]
      }
    }]
  };

  const vendorPerformanceOptions = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      borderColor: 'rgba(201, 162, 39, 0.3)',
      textStyle: { color: '#f8fafc' }
    },
    radar: {
      indicator: [
        { name: 'Quality', max: 100 },
        { name: 'Delivery', max: 100 },
        { name: 'Cost', max: 100 },
        { name: 'Communication', max: 100 },
        { name: 'Compliance', max: 100 }
      ],
      axisName: { color: '#94a3b8' },
      splitLine: { lineStyle: { color: '#1e293b' } },
      splitArea: { areaStyle: { color: ['rgba(30, 41, 59, 0.3)', 'rgba(30, 41, 59, 0.5)'] } }
    },
    series: [{
      type: 'radar',
      data: [
        {
          value: [92, 88, 85, 90, 94],
          name: 'Average Performance',
          areaStyle: { color: 'rgba(201, 162, 39, 0.3)' },
          lineStyle: { color: '#c9a227' },
          itemStyle: { color: '#c9a227' }
        }
      ]
    }]
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-[#c9a227] text-xl">Loading PMO dashboard...</div>
      </div>
    );
  }

  // Calculate KPIs from projects
  const activeProjects = projects.filter(p => p.status === 'active').length;
  const completedProjects = projects.filter(p => p.status === 'completed').length;
  const avgCompletion = projects.length > 0 
    ? Math.round(projects.reduce((acc, p) => acc + (p.completion_percentage || 0), 0) / projects.length) 
    : 0;
  const atRiskProjects = projects.filter(p => p.approval_status === 'pending').length;

  return (
    <div className="space-y-6" data-testid="pmo-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            PMO Operations Dashboard
          </h1>
          <p className="text-slate-400 mt-1">Project lifecycle execution and operational maturity</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Projects in Execution</p>
                <p className="text-3xl font-bold text-[#c9a227] mt-2">{activeProjects}</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-[#c9a227]/10 flex items-center justify-center">
                <FolderKanban className="w-6 h-6 text-[#c9a227]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Stage-Gate Compliance</p>
                <p className="text-3xl font-bold text-green-400 mt-2">{avgCompletion}%</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Pending Approval</p>
                <p className="text-3xl font-bold text-yellow-400 mt-2">{atRiskProjects}</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-yellow-500/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Total Projects</p>
                <p className="text-3xl font-bold text-[#c9a227] mt-2">{projects.length}</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-[#c9a227]/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#c9a227]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Stage-Gate Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ReactECharts option={stageGateChartOptions} style={{ height: '300px' }} />
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Vendor Performance Radar</CardTitle>
          </CardHeader>
          <CardContent>
            <ReactECharts option={vendorPerformanceOptions} style={{ height: '300px' }} />
          </CardContent>
        </Card>
      </div>

      {/* Projects & Vendors */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Project Progress */}
        <Card className="glass-card lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-slate-200">Project Progress Tracker</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {projects?.slice(0, 6).map((project) => (
                <div 
                  key={project.id} 
                  className="p-4 bg-slate-800/30 rounded-sm cursor-pointer hover:bg-slate-800/50 transition-colors"
                  onClick={() => navigate(`/projects/${project.id}`)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <Target className="w-5 h-5 text-[#c9a227]" />
                      <div>
                        <p className="font-medium text-white">{project.name?.length > 35 ? project.name.slice(0, 35) + '...' : project.name}</p>
                        <p className="text-xs text-slate-500">{project.country} • Stage {project.stage_gate || 1}</p>
                      </div>
                    </div>
                    <Badge className={(project.completion_percentage || 0) >= 70 ? 'status-green' : (project.completion_percentage || 0) >= 40 ? 'status-yellow' : 'status-red'}>
                      {project.completion_percentage || 0}%
                    </Badge>
                  </div>
                  <Progress value={project.completion_percentage || 0} className="h-2 bg-slate-700" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Project Managers */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Project Managers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {projects?.slice(0, 5).map((project) => (
                <div key={project.id} className="flex items-center justify-between p-3 bg-slate-800/30 rounded-sm">
                  <div>
                    <p className="font-medium text-white text-sm">{project.project_manager}</p>
                    <p className="text-xs text-slate-500">{project.country}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[#c9a227] font-semibold">{project.completion_percentage || 0}%</p>
                    <Badge className={project.status === 'active' ? 'status-green' : 'status-yellow'}>
                      {project.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PMODashboard;
